/**
 * Roles Management Page
 * CRUD operations for roles
 */

import React, { useState, useEffect } from "react";
import { usePageTitle } from "../contexts/PageTitleContext";
import { rolesService } from "../services/api/roles.service";
import type {
  Role,
  ModuleNode,
  CountyOption,
  LinkRoleCountyRequest,
  RoleCountyLink,
} from "../types/role.types";
import BusyOverlay from "../components/common/BusyOverlay";
import {
  HasPermission,
  ApplicationPermissionId,
  ModuleId,
} from "../utils/permissions";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { REQUIRED_FIELD_MESSAGE } from "../utils/constants";
import { showConfirm } from "../utils/toast";
import "../styles/pages/Roles.css";

export const Roles: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [moduleTree, setModuleTree] = useState<Record<number, ModuleNode[]>>(
    {},
  );
  const [expandedModules, setExpandedModules] = useState<
    Record<number, Set<number>>
  >({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalFormData, setModalFormData] = useState({
    name: "",
    description: "",
  });
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalValidationErrors, setModalValidationErrors] = useState<
    Record<string, string>
  >({});

  const [showEditModal, setShowEditModal] = useState(false);
  const [editRoleId, setEditRoleId] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    description: "",
    isActive: true,
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editValidationErrors, setEditValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showLinkCountyModal, setShowLinkCountyModal] = useState(false);
  const [linkCountyLoading, setLinkCountyLoading] = useState(false);
  const [linkCountyError, setLinkCountyError] = useState("");
  const [counties, setCounties] = useState<CountyOption[]>([]);
  const [roleCountyLinks, setRoleCountyLinks] = useState<RoleCountyLink[]>([]);
  const [linkCountyFormData, setLinkCountyFormData] =
    useState<LinkRoleCountyRequest>({
      roleId: 0,
      countyId: 0,
    });
  const [linkCountyValidationErrors, setLinkCountyValidationErrors] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle("Roles & Permissions", "Manage roles and their permissions");
    fetchRoles();
  }, [setPageTitle]);

  const fetchModuleTree = async (roleId: number) => {
    try {
      const tree = await rolesService.getRolePermissionTree(roleId);
      setModuleTree((prev) => ({ ...prev, [roleId]: tree }));
    } catch (err: any) {
      console.error("Error fetching module tree:", err);
    }
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await rolesService.getRoles();
      setRoles(Array.isArray(data) ? data : []);

      if (Array.isArray(data) && data.length > 0) {
        const treePromises = data.map(async (role) => {
          try {
            const tree = await rolesService.getRolePermissionTree(role.id);
            return { roleId: role.id, tree };
          } catch (err) {
            console.error(`Error fetching tree for role ${role.id}:`, err);
            return { roleId: role.id, tree: [] };
          }
        });

        const trees = await Promise.all(treePromises);
        const treeMap: Record<string, ModuleNode[]> = {};
        trees.forEach(({ roleId, tree }) => {
          treeMap[roleId] = tree;
        });
        setModuleTree(treeMap);
      }
    } catch (err: any) {
      console.error("Error fetching roles:", err);
      setError(
        err?.response?.data?.message || err?.message || "Failed to load roles",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleCountyLinks = async () => {
    try {
      const links = await rolesService.getRoleCountyLinks();
      setRoleCountyLinks(Array.isArray(links) ? links : []);
    } catch {
      setRoleCountyLinks([]);
    }
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError("");
    const errors: Record<string, string> = {};

    if (!modalFormData.name.trim()) {
      errors.name = REQUIRED_FIELD_MESSAGE;
    }

    if (!modalFormData.description.trim()) {
      errors.description = REQUIRED_FIELD_MESSAGE;
    }

    setModalValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Create Role",
      `Are you sure you want to create the role "${modalFormData.name}"?`,
      "Yes, create",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setModalLoading(true);
      await rolesService.createRole({
        ...modalFormData,
        permissions: [],
      });
      setShowAddModal(false);
      setModalFormData({ name: "", description: "" });
      await fetchRoles();
    } catch (err: any) {
      setModalError(err?.response?.data?.message || "Failed to create role");
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditRole = async (roleId: number) => {
    try {
      const role = await rolesService.getRoleById(roleId);
      setEditRoleId(roleId);
      setEditFormData({
        name: role.name,
        description: role.description,
        isActive: role.isActive,
      });
      setShowEditModal(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to load role");
    }
  };

  const handleUpdateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError("");
    const errors: Record<string, string> = {};

    if (!editFormData.name.trim()) {
      errors.name = REQUIRED_FIELD_MESSAGE;
    }

    if (!editFormData.description.trim()) {
      errors.description = REQUIRED_FIELD_MESSAGE;
    }

    setEditValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    if (!editRoleId) return;

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Update Role",
      `Are you sure you want to update the role "${editFormData.name}"?`,
      "Yes, update",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setEditLoading(true);
      const updateData = {
        id: Number(editRoleId),
        name: editFormData.name,
        description: editFormData.description,
        isActive: editFormData.isActive,
      };
      await rolesService.updateRole(Number(editRoleId), updateData);
      setShowEditModal(false);
      setEditFormData({ name: "", description: "", isActive: true });
      setEditRoleId(null);
      await fetchRoles();
    } catch (err: any) {
      console.error("Update error:", err);
      setEditError(err?.response?.data?.message || "Failed to update role");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await rolesService.deleteRole(id);
      setRoles(roles.filter((role) => role.id !== id));
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to delete role");
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      const updatedRole = await rolesService.toggleRoleStatus(
        id,
        !currentStatus,
      );
      setRoles(roles.map((role) => (role.id === id ? updatedRole : role)));
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to update role status");
    }
  };

  const toggleRow = (roleId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(roleId)) {
      newExpanded.delete(roleId);
    } else {
      newExpanded.add(roleId);
      fetchModuleTree(roleId);
      if (!expandedModules[roleId]) {
        setExpandedModules((prev) => ({
          ...prev,
          [roleId]: new Set(),
        }));
      }
    }
    setExpandedRows(newExpanded);
  };

  const toggleModule = (roleId: number, moduleId: number) => {
    setExpandedModules((prev) => {
      const roleModules = prev[roleId] || new Set();
      const newModules = new Set(roleModules);
      if (newModules.has(moduleId)) {
        newModules.delete(moduleId);
      } else {
        newModules.add(moduleId);
      }
      return { ...prev, [roleId]: newModules };
    });
  };

  const expandAllModules = (roleId: number) => {
    const tree = moduleTree[roleId];
    if (!tree) return;

    const allModuleIds = new Set(
      tree.map((module: any) => module.moduleId || module.id),
    );
    setExpandedModules((prev) => ({
      ...prev,
      [roleId]: allModuleIds,
    }));
  };

  const collapseAllModules = (roleId: number) => {
    setExpandedModules((prev) => ({
      ...prev,
      [roleId]: new Set(),
    }));
  };

  const handlePermissionToggle = async (
    roleId: number,
    moduleId: number,
    permissionId: number,
    isCurrentlyEnabled: boolean,
  ) => {
    try {
      await rolesService.toggleRolePermission(
        roleId,
        moduleId,
        permissionId,
        !isCurrentlyEnabled,
      );

      await Promise.all([fetchModuleTree(roleId), fetchRoles()]);
    } catch (err: any) {
      console.error("Permission toggle error:", err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.title ||
          err?.message ||
          "Failed to update permission",
      );
    }
  };

  const handleLinkRoleToCounty = (roleId: number) => {
    setLinkCountyFormData({ roleId, countyId: 0 });
    setLinkCountyError("");
    setLinkCountyValidationErrors({});
    setShowLinkCountyModal(true);
    void fetchCounties();
  };

  const fetchCounties = async () => {
    try {
      const data = await rolesService.getCounties();
      setCounties(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setLinkCountyError(err?.message || "Failed to load counties");
      setCounties([]);
    }
  };

  const handleSubmitLinkCounty = async (e: React.FormEvent) => {
    e.preventDefault();
    setLinkCountyError("");
    const errors: Record<string, string> = {};

    if (!linkCountyFormData.roleId || linkCountyFormData.roleId <= 0) {
      errors.roleId = REQUIRED_FIELD_MESSAGE;
    }

    if (!linkCountyFormData.countyId || linkCountyFormData.countyId <= 0) {
      errors.countyId = REQUIRED_FIELD_MESSAGE;
    }

    setLinkCountyValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Link Role to County",
      "Are you sure you want to link this role to the selected county?",
      "Yes, link",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setLinkCountyLoading(true);
      await rolesService.linkRoleToCounty(linkCountyFormData);
      await fetchRoleCountyLinks();
      setShowLinkCountyModal(false);
      setLinkCountyFormData({ roleId: 0, countyId: 0 });
      setLinkCountyValidationErrors({});
      setLinkCountyError("");
    } catch (err: any) {
      setLinkCountyError(err?.message || "Failed to link role to county");
    } finally {
      setLinkCountyLoading(false);
    }
  };

  const getRoleCountyLink = (roleId: number) => {
    return roleCountyLinks.find((link) => link.roleId === roleId);
  };

  const handleUnlinkRoleFromCounty = async (roleId: number) => {
    const existingLink = getRoleCountyLink(roleId);
    if (!existingLink) return;

    const confirmed = await showConfirm(
      "Unlink Role from County?",
      "Are you sure you want to unlink this role from its county?",
      "Yes, unlink",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setLinkCountyLoading(true);
      await rolesService.unlinkRoleFromCounty(existingLink.id, {
        roleId,
        countyId: existingLink.countyId,
      });
      await fetchRoleCountyLinks();
    } catch (err: any) {
      setError(err?.message || "Failed to unlink role from county");
    } finally {
      setLinkCountyLoading(false);
    }
  };

  const filteredRoles = roles.filter(
    (role) =>
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const selectedRoleName =
    roles.find((role) => role.id === linkCountyFormData.roleId)?.name || "";

  useEffect(() => {
    void fetchRoleCountyLinks();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getPermissionCount = (roleId: number) => {
    const tree = moduleTree[roleId];
    if (!tree || tree.length === 0) {
      return { enabled: 0, total: 0 };
    }

    let enabled = 0;
    let total = 0;

    tree.forEach((module) => {
      if (module.permissions) {
        total += module.permissions.length;
        enabled += module.permissions.filter(
          (p: any) => p.isAssigned || p.isEnabled,
        ).length;
      }
    });

    return { enabled, total };
  };

  // Check if user has permission to view roles
  if (!HasPermission(ModuleId.Roles, ApplicationPermissionId.View)) {
    return (
      <div
        className={`dashboard-body ${
          sidebarOpen ? "sidebar-open" : "sidebar-closed"
        }`}
      >
        <Topbar
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          sidebarOpen={sidebarOpen}
        />
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content">
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>You do not have permission to view roles</span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="roles-page">
          <BusyOverlay
            isVisible={loading || modalLoading || editLoading}
            message={
              modalLoading
                ? "Creating role..."
                : editLoading
                  ? "Updating role..."
                  : "Loading roles..."
            }
          />

          <div className="roles-content">
            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                <span>{error}</span>
                <button onClick={() => setError("")} className="alert-close">
                  ✕
                </button>
              </div>
            )}

            <div className="roles-toolbar">
              <div className="search-box">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search roles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="roles-stats">
                <span className="stat-badge">
                  Total: <strong>{roles.length}</strong>
                </span>
                <span className="stat-badge stat-badge--success">
                  Active:{" "}
                  <strong>{roles.filter((r) => r.isActive).length}</strong>
                </span>
              </div>
              <div>
                {HasPermission(
                  ModuleId.Roles,
                  ApplicationPermissionId.Create,
                ) && (
                  <button
                    className="btn btn--primary"
                    onClick={() => setShowAddModal(true)}
                  >
                    <span>➕</span> Add New Role
                  </button>
                )}
              </div>
            </div>

            {filteredRoles.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state__icon">📋</div>
                <h3 className="empty-state__title">No roles found</h3>
                <p className="empty-state__message">
                  {searchTerm
                    ? "Try adjusting your search criteria"
                    : "Get started by creating your first role"}
                </p>
                {!searchTerm &&
                  HasPermission(
                    ModuleId.Roles,
                    ApplicationPermissionId.Create,
                  ) && (
                    <button
                      className="btn btn--primary"
                      onClick={() => setShowAddModal(true)}
                    >
                      Add New Role
                    </button>
                  )}
              </div>
            ) : (
              <div className="roles-table-container">
                <table className="roles-table">
                  <thead>
                    <tr>
                      <th style={{ width: "40px" }}></th>
                      <th>Role Name</th>
                      <th>Description</th>
                      <th>Permissions</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th style={{ width: "150px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRoles.map((role) => (
                      <React.Fragment key={role.id}>
                        <tr
                          className={
                            expandedRows.has(role.id) ? "expanded" : ""
                          }
                        >
                          <td>
                            <button
                              className="expand-btn"
                              onClick={() => toggleRow(role.id)}
                              title="Expand to manage permissions"
                            >
                              {expandedRows.has(role.id) ? "▼" : "▶"}
                            </button>
                          </td>
                          <td className="role-name">
                            {(() => {
                              const linkedCounty = getRoleCountyLink(role.id);
                              const countyName = linkedCounty?.countyName;
                              return countyName
                                ? `${role.name} (Linked to ${countyName})`
                                : role.name;
                            })()}
                          </td>
                          <td className="role-description">
                            {role.description}
                          </td>
                          <td className="role-permissions">
                            <span className="permissions-badge">
                              {(() => {
                                const count = getPermissionCount(role.id);
                                return count.total > 0
                                  ? `${count.enabled} / ${count.total}`
                                  : role.permissions?.length || 0;
                              })()}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`status-badge ${
                                role.isActive
                                  ? "status-badge--active"
                                  : "status-badge--inactive"
                              }`}
                            >
                              {role.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="role-created">
                            {role.createdAt
                              ? formatDate(role.createdAt)
                              : "N/A"}
                          </td>
                          <td className="role-actions">
                            {HasPermission(
                              ModuleId.Roles,
                              ApplicationPermissionId.Edit,
                            ) &&
                              (getRoleCountyLink(role.id) ? (
                                <button
                                  className="icon-btn icon-btn--warning"
                                  onClick={() =>
                                    handleUnlinkRoleFromCounty(role.id)
                                  }
                                  title={`Linked County: ${getRoleCountyLink(role.id)?.countyName || `County #${getRoleCountyLink(role.id)?.countyId ?? "Unknown"}`}`}
                                  disabled={linkCountyLoading}
                                >
                                  🔓
                                </button>
                              ) : (
                                <button
                                  className="icon-btn"
                                  onClick={() =>
                                    handleLinkRoleToCounty(role.id)
                                  }
                                  title="Link Role to County"
                                >
                                  🔗
                                </button>
                              ))}
                            {HasPermission(
                              ModuleId.Roles,
                              ApplicationPermissionId.Edit,
                            ) && (
                              <button
                                className="icon-btn icon-btn--edit"
                                onClick={() => handleEditRole(role.id)}
                                title="Edit"
                              >
                                ✏️
                              </button>
                            )}
                            {HasPermission(
                              ModuleId.Roles,
                              ApplicationPermissionId.Edit,
                            ) && (
                              <button
                                className={`icon-btn ${
                                  role.isActive
                                    ? "icon-btn--warning"
                                    : "icon-btn--success"
                                }`}
                                onClick={() =>
                                  handleToggleStatus(role.id, role.isActive)
                                }
                                title={
                                  role.isActive ? "Deactivate" : "Activate"
                                }
                              >
                                {role.isActive ? "🔒" : "🔓"}
                              </button>
                            )}
                            {HasPermission(
                              ModuleId.Roles,
                              ApplicationPermissionId.Delete,
                            ) && (
                              <button
                                className="icon-btn icon-btn--delete"
                                onClick={() => setDeleteConfirm(role.id)}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            )}
                          </td>
                        </tr>
                        {expandedRows.has(role.id) && (
                          <tr className="expanded-row">
                            <td colSpan={7}>
                              <div className="permissions-panel">
                                <div className="permissions-panel__header">
                                  <h4 className="permissions-panel__title">
                                    Manage Permissions for {role.name}
                                  </h4>
                                  <div className="permissions-panel__actions">
                                    {HasPermission(
                                      ModuleId.Roles,
                                      ApplicationPermissionId.Edit,
                                    ) && (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn--secondary btn--sm"
                                          onClick={() =>
                                            expandAllModules(role.id)
                                          }
                                          title="Expand all modules"
                                        >
                                          Expand All
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn--secondary btn--sm"
                                          onClick={() =>
                                            collapseAllModules(role.id)
                                          }
                                          title="Collapse all modules"
                                        >
                                          Collapse All
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="permissions-tree">
                                  {moduleTree[role.id]?.map((module) => {
                                    const moduleIdValue =
                                      (module as any).moduleId || module.id;
                                    const roleExpandedModules =
                                      expandedModules[role.id] || new Set();
                                    const isModuleExpanded =
                                      roleExpandedModules.has(moduleIdValue);
                                    const enabledCount =
                                      module.permissions.filter(
                                        (p: any) => p.isAssigned || p.isEnabled,
                                      ).length;

                                    return (
                                      <div
                                        key={moduleIdValue}
                                        className="tree-node"
                                      >
                                        <div
                                          className="tree-node-header"
                                          onClick={() => {
                                            toggleModule(
                                              role.id,
                                              moduleIdValue,
                                            );
                                          }}
                                        >
                                          <div className="tree-node-title">
                                            <span className="tree-icon">
                                              {isModuleExpanded ? "🔓" : "🔒"}
                                            </span>
                                            <button
                                              type="button"
                                              className="tree-expand-btn"
                                            >
                                              {isModuleExpanded ? "▼" : "▶"}
                                            </button>
                                            <span className="tree-module-name">
                                              {module.name ||
                                                (module as any).moduleName ||
                                                (module as any).ModuleName ||
                                                "Unnamed Module"}
                                            </span>
                                          </div>
                                          <span className="tree-count-badge">
                                            {enabledCount} /{" "}
                                            {module.permissions.length}
                                          </span>
                                        </div>
                                        {isModuleExpanded && (
                                          <div className="tree-node-children">
                                            {module.permissions.map(
                                              (modulePermission: any) => {
                                                const permObj: any =
                                                  modulePermission.permission ||
                                                  modulePermission;

                                                const plainName =
                                                  permObj?.permissionPlainName ||
                                                  permObj?.PlainName ||
                                                  permObj?.plainName ||
                                                  permObj?.Name ||
                                                  permObj?.name ||
                                                  (modulePermission as any)
                                                    ?.applicationPermission
                                                    ?.permissionPlainName ||
                                                  (modulePermission as any)
                                                    ?.applicationPermission
                                                    ?.PlainName ||
                                                  (modulePermission as any)
                                                    ?.applicationPermission
                                                    ?.plainName ||
                                                  "Unnamed Permission";
                                                const codeName =
                                                  permObj?.permissionCodeName ||
                                                  permObj?.CodeName ||
                                                  permObj?.codeName ||
                                                  permObj?.Code ||
                                                  permObj?.code ||
                                                  (modulePermission as any)
                                                    ?.applicationPermission
                                                    ?.permissionCodeName ||
                                                  (modulePermission as any)
                                                    ?.applicationPermission
                                                    ?.CodeName ||
                                                  (modulePermission as any)
                                                    ?.applicationPermission
                                                    ?.codeName ||
                                                  "";

                                                const moduleIdForToggle =
                                                  (module as any).moduleId ||
                                                  module.id;
                                                const permissionId =
                                                  (modulePermission as any)
                                                    .permissionId ||
                                                  permObj?.id ||
                                                  (modulePermission as any)
                                                    ?.applicationPermission?.id;

                                                const isEnabled =
                                                  (modulePermission as any)
                                                    .isAssigned ||
                                                  modulePermission.isEnabled ||
                                                  false;
                                                return (
                                                  <div
                                                    key={
                                                      (modulePermission as any)
                                                        .modulePermissionId ||
                                                      modulePermission.id
                                                    }
                                                    className="tree-leaf"
                                                  >
                                                    <div className="tree-leaf-content">
                                                      <div className="tree-leaf-icon">
                                                        ✦
                                                      </div>
                                                      <div className="tree-leaf-info">
                                                        <span className="tree-permission-name">
                                                          {plainName ||
                                                            "Unnamed Permission"}
                                                        </span>
                                                        <span className="tree-permission-description">
                                                          {codeName ||
                                                            "No code"}
                                                        </span>
                                                      </div>
                                                      {HasPermission(
                                                        ModuleId.Roles,
                                                        ApplicationPermissionId.Edit,
                                                      ) && (
                                                        <label className="switch">
                                                          <input
                                                            type="checkbox"
                                                            checked={isEnabled}
                                                            onChange={() =>
                                                              handlePermissionToggle(
                                                                role.id,
                                                                moduleIdForToggle,
                                                                permissionId,
                                                                isEnabled,
                                                              )
                                                            }
                                                          />
                                                          <span className="slider"></span>
                                                        </label>
                                                      )}
                                                    </div>
                                                  </div>
                                                );
                                              },
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {(!moduleTree[role.id] ||
                                    moduleTree[role.id].length === 0) && (
                                    <div className="no-permissions-tree">
                                      <p>No permissions available</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        {deleteConfirm === role.id && (
                          <tr className="delete-confirm-row">
                            <td colSpan={7}>
                              <div className="delete-confirm">
                                <p>
                                  ⚠️ Are you sure you want to delete the role "
                                  {role.name}"?
                                </p>
                                <div className="delete-confirm__actions">
                                  <button
                                    className="btn btn--sm btn--danger"
                                    onClick={() => handleDelete(role.id)}
                                  >
                                    Delete
                                  </button>
                                  <button
                                    className="btn btn--sm btn--outline"
                                    onClick={() => setDeleteConfirm(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Add New Role Modal */}
          {showAddModal && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div className="modal-content modal-content--side-panel">
                <div className="modal-header">
                  <h2 className="modal-title">Add New Role</h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowAddModal(false);
                      setModalFormData({ name: "", description: "" });
                      setModalError("");
                    }}
                    disabled={modalLoading}
                  >
                    ✕
                  </button>
                </div>

                <form className="modal-form" onSubmit={handleCreateRole}>
                  {modalError && (
                    <div className="alert alert-error">
                      <span className="alert-icon">⚠️</span>
                      <span>{modalError}</span>
                      <button
                        type="button"
                        onClick={() => setModalError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <div
                    className={`form-group ${
                      modalFormData.name ? "has-value" : ""
                    } ${modalValidationErrors.name ? "form-group--error" : ""}`}
                  >
                    <input
                      type="text"
                      id="modal-name"
                      className={`form-input ${modalValidationErrors.name ? "form-input--error" : ""}`}
                      placeholder=" "
                      value={modalFormData.name}
                      onChange={(e) => {
                        setModalFormData({
                          ...modalFormData,
                          name: e.target.value,
                        });
                        if (modalValidationErrors.name) {
                          setModalValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.name;
                            return newErrors;
                          });
                        }
                      }}
                      disabled={modalLoading}
                      required
                      autoFocus
                    />
                    <label htmlFor="modal-name" className="form-label">
                      Role Name <span className="required">*</span>
                    </label>
                    {modalValidationErrors.name && (
                      <div className="field-error">
                        {modalValidationErrors.name}
                      </div>
                    )}
                  </div>

                  <div
                    className={`form-group ${
                      modalFormData.description ? "has-value" : ""
                    } ${modalValidationErrors.description ? "form-group--error" : ""}`}
                  >
                    <textarea
                      id="modal-description"
                      className={`form-textarea form-input ${modalValidationErrors.description ? "form-input--error" : ""}`}
                      placeholder=" "
                      rows={4}
                      value={modalFormData.description}
                      onChange={(e) => {
                        setModalFormData({
                          ...modalFormData,
                          description: e.target.value,
                        });
                        if (modalValidationErrors.description) {
                          setModalValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.description;
                            return newErrors;
                          });
                        }
                      }}
                      disabled={modalLoading}
                      required
                    />
                    <label htmlFor="modal-description" className="form-label">
                      Description <span className="required">*</span>
                    </label>
                    {modalValidationErrors.description && (
                      <div className="field-error">
                        {modalValidationErrors.description}
                      </div>
                    )}
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={() => {
                        setShowAddModal(false);
                        setModalFormData({ name: "", description: "" });
                        setModalError("");
                        setModalValidationErrors({});
                      }}
                      disabled={modalLoading}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={modalLoading}
                    >
                      {modalLoading ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          Creating...
                        </>
                      ) : (
                        "Create Role"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Link Role to County Panel */}
          {showLinkCountyModal && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div className="modal-content modal-content--side-panel modal-content--county-link">
                <div className="modal-header">
                  <h2 className="modal-title">Link to County</h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowLinkCountyModal(false);
                      setLinkCountyFormData({ roleId: 0, countyId: 0 });
                      setLinkCountyValidationErrors({});
                      setLinkCountyError("");
                    }}
                    disabled={linkCountyLoading}
                  >
                    ✕
                  </button>
                </div>

                <form className="modal-form" onSubmit={handleSubmitLinkCounty}>
                  {linkCountyError && (
                    <div className="alert alert-error">
                      <span className="alert-icon">⚠️</span>
                      <span>{linkCountyError}</span>
                      <button
                        type="button"
                        onClick={() => setLinkCountyError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <div
                    className={`form-group has-value ${linkCountyValidationErrors.roleId ? "form-group--error" : ""}`}
                  >
                    <input
                      type="text"
                      className={`form-input ${linkCountyValidationErrors.roleId ? "form-input--error" : ""}`}
                      value={selectedRoleName}
                      readOnly
                      placeholder=" "
                      style={{
                        background: "#f1f1f1",
                        cursor: "not-allowed",
                      }}
                    />
                    <label className="form-label">
                      Role Name <span className="required">*</span>
                    </label>
                    {linkCountyValidationErrors.roleId && (
                      <div className="field-error">
                        {linkCountyValidationErrors.roleId}
                      </div>
                    )}
                  </div>

                  <div
                    className={`form-group has-value ${linkCountyValidationErrors.countyId ? "form-group--error" : ""}`}
                  >
                    <select
                      className={`form-input ${linkCountyValidationErrors.countyId ? "form-input--error" : ""}`}
                      value={linkCountyFormData.countyId || ""}
                      onChange={(e) => {
                        setLinkCountyFormData((prev) => ({
                          ...prev,
                          countyId: parseInt(e.target.value, 10) || 0,
                        }));
                        if (linkCountyValidationErrors.countyId) {
                          setLinkCountyValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.countyId;
                            return newErrors;
                          });
                        }
                      }}
                      required
                      disabled={linkCountyLoading}
                    >
                      <option value="">Select County</option>
                      {counties.map((county) => (
                        <option key={county.id} value={county.id}>
                          {county.name}
                        </option>
                      ))}
                    </select>
                    <label className="form-label">
                      County <span className="required">*</span>
                    </label>
                    {linkCountyValidationErrors.countyId && (
                      <div className="field-error">
                        {linkCountyValidationErrors.countyId}
                      </div>
                    )}
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={() => {
                        setShowLinkCountyModal(false);
                        setLinkCountyFormData({ roleId: 0, countyId: 0 });
                        setLinkCountyValidationErrors({});
                        setLinkCountyError("");
                      }}
                      disabled={linkCountyLoading}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={linkCountyLoading}
                    >
                      {linkCountyLoading ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          Linking...
                        </>
                      ) : (
                        "Link County"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Role Modal */}
          {showEditModal && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div className="modal-content modal-content--side-panel">
                <div className="modal-header">
                  <h2 className="modal-title">Edit Role</h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditFormData({
                        name: "",
                        description: "",
                        isActive: true,
                      });
                      setEditError("");
                      setEditRoleId(null);
                    }}
                    disabled={editLoading}
                  >
                    ✕
                  </button>
                </div>

                <form className="modal-form" onSubmit={handleUpdateRole}>
                  {editError && (
                    <div className="alert alert-error">
                      <span className="alert-icon">⚠️</span>
                      <span>{editError}</span>
                      <button
                        type="button"
                        onClick={() => setEditError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <div
                    className={`form-group ${editFormData.name ? "has-value" : ""} ${editValidationErrors.name ? "form-group--error" : ""}`}
                  >
                    <input
                      type="text"
                      id="edit-name"
                      className={`form-input ${editValidationErrors.name ? "form-input--error" : ""}`}
                      placeholder=" "
                      value={editFormData.name}
                      onChange={(e) => {
                        setEditFormData({
                          ...editFormData,
                          name: e.target.value,
                        });
                        if (editValidationErrors.name) {
                          setEditValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.name;
                            return newErrors;
                          });
                        }
                      }}
                      disabled={editLoading}
                      required
                      autoFocus
                    />
                    <label htmlFor="edit-name" className="form-label">
                      Role Name <span className="required">*</span>
                    </label>
                    {editValidationErrors.name && (
                      <div className="field-error">
                        {editValidationErrors.name}
                      </div>
                    )}
                  </div>

                  <div
                    className={`form-group ${
                      editFormData.description ? "has-value" : ""
                    } ${editValidationErrors.description ? "form-group--error" : ""}`}
                  >
                    <textarea
                      id="edit-description"
                      className={`form-textarea form-input ${editValidationErrors.description ? "form-input--error" : ""}`}
                      placeholder=" "
                      rows={4}
                      value={editFormData.description}
                      onChange={(e) => {
                        setEditFormData({
                          ...editFormData,
                          description: e.target.value,
                        });
                        if (editValidationErrors.description) {
                          setEditValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.description;
                            return newErrors;
                          });
                        }
                      }}
                      disabled={editLoading}
                      required
                    />
                    <label htmlFor="edit-description" className="form-label">
                      Description <span className="required">*</span>
                    </label>
                    {editValidationErrors.description && (
                      <div className="field-error">
                        {editValidationErrors.description}
                      </div>
                    )}
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditFormData({
                          name: "",
                          description: "",
                          isActive: true,
                        });
                        setEditError("");
                        setEditValidationErrors({});
                        setEditRoleId(null);
                      }}
                      disabled={editLoading}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={editLoading}
                    >
                      {editLoading ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          Updating...
                        </>
                      ) : (
                        "Update Role"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Roles;
